import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/base/Button';
import Input from '../../components/base/Input';
import { supabase } from '../../lib/supabase';
import { logger } from '../../utils/logger';

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    logger.debug('[ForgotPassword] Iniciando processo de recuperação de senha...');
    logger.sensitive('[ForgotPassword] Email:', email);
    
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      // Em desenvolvimento, fixar para http://localhost:3000 para evitar portas aleatórias
      const baseUrl = import.meta.env.DEV ? 'http://localhost:3000' : window.location.origin;
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${baseUrl}/reset-password`
      });

      if (resetError) {
        logger.error('[ForgotPassword] Erro ao enviar email:', resetError);
        throw resetError;
      }

      logger.success('[ForgotPassword] Email de recuperação enviado com sucesso');
      setSuccess(true);
    } catch (error: any) {
      logger.error('[ForgotPassword] Erro no processo:', error);
      setError(error.message || 'Erro ao enviar e-mail de recuperação. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center px-6 py-8">
      {/* Header com botão voltar */}
      <div className="w-full flex justify-start mb-6">
        <button
          onClick={goBack}
          className="w-10 h-10 bg-gray-50 rounded-lg flex items-center justify-center shadow-soft hover:bg-gray-100 transition-colors border border-gray-200"
        >
          <i className="ri-arrow-left-line text-gray-600 text-xl"></i>
        </button>
      </div>

      {/* Logo e Header */}
      <div className="text-center mb-8">
        <div className="w-20 h-20 rounded-card flex items-center justify-center mb-4 mx-auto shadow-card overflow-hidden">
          <img 
            src="/Imagens/logo 2.0.png"
            alt="Logo Magistral"
            className="w-full h-full object-contain"
          />
        </div>
        <h1 className="text-3xl font-bold text-gray-800 font-sans" style={{ 
          fontWeight: 700,
          textShadow: '1px 2px 4px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.05)'
        }}>
          Recuperar Senha
        </h1>
        <p className="text-gray-600 mt-2 font-sans" style={{ fontWeight: 500 }}>
          {success 
            ? 'E-mail enviado com sucesso!'
            : 'Digite seu e-mail para receber instruções'}
        </p>
      </div>

      {/* Formulário ou Mensagem de Sucesso */}
      <div className="w-full max-w-sm bg-white rounded-card p-6 shadow-card border border-gray-100">
        {success ? (
          <div className="text-center space-y-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <i className="ri-mail-send-line text-green-600 text-3xl"></i>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-800 mb-2 font-sans" style={{ fontWeight: 700 }}>
                Verifique seu e-mail
              </h2>
              <p className="text-gray-600 text-sm font-sans" style={{ fontWeight: 500 }}>
                Enviamos um link de recuperação para:
              </p>
              <p className="text-green-600 font-medium mt-1 font-sans" style={{ fontWeight: 600 }}>
                {email}
              </p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
              <p className="text-blue-800 text-sm font-sans" style={{ fontWeight: 500 }}>
                <i className="ri-information-line mr-1"></i>
                Clique no link do e-mail para criar uma nova senha. O link expira em 1 hora.
              </p>
            </div>
            <Button 
              onClick={goBack} 
              className="w-full mt-4"
              variant="outline"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              Voltar para o Login
            </Button>
          </div>
        ) : (
          <>
            <div className="text-center mb-6">
              <h2 className="text-xl font-semibold text-gray-800 font-sans" style={{ fontWeight: 700 }}>
                Esqueceu sua senha?
              </h2>
              <p className="text-gray-600 text-sm mt-1 font-sans" style={{ fontWeight: 500 }}>
                Sem problemas! Digite seu e-mail abaixo.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}
              
              <Input
                label="E-mail"
                type="email"
                placeholder="seu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mt-4">
                <p className="text-gray-600 text-xs font-sans" style={{ fontWeight: 500 }}>
                  <i className="ri-shield-check-line mr-1 text-green-600"></i>
                  Enviaremos um link seguro para redefinir sua senha.
                </p>
              </div>

              <Button type="submit" size="lg" className="w-full mt-6" disabled={loading}>
                {loading ? (
                  <>
                    <i className="ri-loader-4-line mr-2 animate-spin"></i>
                    Enviando...
                  </>
                ) : (
                  <>
                    <i className="ri-mail-send-line mr-2"></i>
                    Enviar Link de Recuperação
                  </>
                )}
              </Button>
            </form>

            <div className="text-center mt-6">
              <p className="text-gray-600 text-sm font-sans" style={{ fontWeight: 500 }}>
                Lembrou sua senha?{' '}
                <button
                  onClick={goBack}
                  className="text-green-500 font-medium hover:text-green-600 transition-colors font-sans" style={{ fontWeight: 700 }}
                >
                  Voltar para o Login
                </button>
              </p>
            </div>
          </>
        )}
      </div>

      {/* Mensagem Motivacional */}
      {!success && (
        <div className="text-center mt-6">
          <p className="text-gray-500 text-sm font-sans" style={{ fontWeight: 500 }}>
            🔒 Seus dados estão seguros conosco
          </p>
        </div>
      )}
    </div>
  );
}
